import React, { useState, useMemo } from 'react';
import { CreditCard, QrCode, Barcode, Loader2, CheckCircle2, ShieldCheck, AlertCircle, ArrowLeft, Ticket, Check, X, ChevronDown } from 'lucide-react';
import { PLAN_LIMITS } from '../types';
import { supabase } from '../supabase';

interface PaymentStepProps {
  plan: 'essential' | 'advanced' | 'pro';
  billingCycle: 'monthly' | 'yearly';
  onSuccess: (asaasData: any) => void;
  onBack: () => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({ plan, billingCycle, onSuccess, onBack }) => {
  const [method, setMethod] = useState<'CREDIT_CARD' | 'PIX' | 'BOLETO'>('CREDIT_CARD');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Parcelamento
  const [installments, setInstallments] = useState(1);
  
  // Cupom
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponError, setCouponError] = useState<string | null>(null);

  const [billingInfo, setBillingInfo] = useState({
    name: '',
    cpfCnpj: '',
    email: '',
    mobilePhone: '',
    postalCode: '',
    addressNumber: '',
  });

  const [cardInfo, setCardInfo] = useState({
    holderName: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    ccv: '',
  });

  const planInfo = PLAN_LIMITS[plan];
  const basePrice = billingCycle === 'monthly' ? planInfo.priceMonthly : planInfo.priceYearly;

  const finalPrice = useMemo(() => {
    if (!appliedCoupon) return basePrice;
    let discount = 0;
    if (appliedCoupon.type === 'percent') {
      discount = basePrice * (appliedCoupon.value / 100);
    } else {
      discount = appliedCoupon.value;
    }
    return Math.max(0, basePrice - discount);
  }, [basePrice, appliedCoupon]);

  const installmentValue = useMemo(() => {
    return finalPrice / installments;
  }, [finalPrice, installments]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.toUpperCase().trim())
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setCouponError("Cupom inválido ou expirado.");
        return;
      }

      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setCouponError("Este cupom expirou.");
        return;
      }

      setAppliedCoupon(data);
    } catch (e: any) {
      setCouponError(e.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleProcessPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1. Criar Cliente via Proxy
      const { data: customer, error: cErr } = await supabase.functions.invoke('asaas-proxy', {
        body: { 
          endpoint: '/customers', 
          method: 'POST', 
          payload: billingInfo 
        }
      });
      
      if (cErr) throw new Error("Erro de rede ao conectar com o gateway.");
      if (!customer.ok) throw new Error(customer.asaas_body?.errors?.[0]?.description || "Erro ao cadastrar dados de faturamento.");

      // 2. Criar Cobrança via Proxy
      const paymentPayload: any = {
        customer: customer.id,
        billingType: method,
        value: finalPrice,
        dueDate: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
        description: `Assinatura Plano ${planInfo.label} (${billingCycle === 'monthly' ? 'Mensal' : 'Anual'}) - Gabinete Digital`,
      };

      // Adicionar lógica de parcelamento para anual no cartão
      if (method === 'CREDIT_CARD' && billingCycle === 'yearly' && installments > 1) {
          paymentPayload.installmentCount = installments;
          paymentPayload.installmentValue = installmentValue;
      }

      if (method === 'CREDIT_CARD') {
        paymentPayload.creditCard = cardInfo;
        paymentPayload.creditCardHolderInfo = {
          name: billingInfo.name,
          email: billingInfo.email,
          cpfCnpj: billingInfo.cpfCnpj,
          postalCode: billingInfo.postalCode,
          addressNumber: billingInfo.addressNumber,
          phone: billingInfo.mobilePhone
        };
      }

      const { data: paymentResult, error: pErr } = await supabase.functions.invoke('asaas-proxy', {
        body: { 
          endpoint: '/payments', 
          method: 'POST', 
          payload: paymentPayload 
        }
      });

      if (pErr) throw new Error("Erro de rede ao processar cobrança.");
      if (!paymentResult.ok) throw new Error(paymentResult.asaas_body?.errors?.[0]?.description || "O pagamento foi recusado pelo gateway.");

      // 3. PIX Data se necessário via Proxy
      let pixData = null;
      if (method === 'PIX') {
        const { data: pixRes } = await supabase.functions.invoke('asaas-proxy', {
          body: { 
            endpoint: `/payments/${paymentResult.id}/pixQrCode`, 
            method: 'GET' 
          }
        });
        if (pixRes.ok) pixData = pixRes;
      }

      // Se houver cupom, registrar redenção
      if (appliedCoupon) {
        try {
          await supabase.from('coupon_redemptions').insert([{
             coupon_id: appliedCoupon.id,
             user_id: (await supabase.auth.getUser()).data.user?.id,
             discount_snapshot: { calculated_discount: basePrice - finalPrice }
          }]);
        } catch(e) {}
      }

      onSuccess({
        customer,
        payment: paymentResult,
        pix: pixData,
        billingInfo
      });

    } catch (err: any) {
      setError(err.message || "Erro inesperado ao processar assinatura.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4">
      <div className="bg-white dark:bg-navy-900 rounded-[40px] shadow-2xl border border-neutral-200 dark:border-navy-800 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-5 h-full">
          <div className="lg:col-span-2 bg-navy-950 p-10 text-white flex flex-col justify-between">
            <div>
               <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 text-xs font-black uppercase tracking-widest transition-colors">
                  <ArrowLeft size={14} /> Voltar aos planos
               </button>
               <p className="text-[10px] font-black uppercase tracking-[0.2em] text-parliament-400 mb-2">Checkout Seguro</p>
               <h3 className="text-3xl font-black tracking-tighter mb-6">Assinatura do Gabinete</h3>
               
               <div className="space-y-4 bg-white/5 p-6 rounded-3xl border border-white/10">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-400">Plano / Ciclo</span>
                    <span className="text-xs font-black uppercase text-parliament-400">{planInfo.label} ({billingCycle === 'monthly' ? 'Mensal' : 'Anual'})</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-slate-400">Preço Base</span>
                    <span className="text-xs font-bold">R$ {basePrice.toFixed(2).replace('.', ',')}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between items-center text-emerald-400">
                      <span className="text-xs font-bold">Desconto ({appliedCoupon.code})</span>
                      <span className="text-xs font-black">- R$ {(basePrice - finalPrice).toFixed(2).replace('.', ',')}</span>
                    </div>
                  )}
                  <div className="h-px bg-white/10 w-full"></div>
                  
                  {installments > 1 && method === 'CREDIT_CARD' ? (
                     <div className="flex flex-col items-end">
                        <span className="text-[10px] font-black text-parliament-400 uppercase tracking-widest">Valor da Parcela</span>
                        <p className="text-3xl font-black text-white">{installments}x de R$ {installmentValue.toFixed(2).replace('.', ',')}</p>
                        <p className="text-[10px] text-slate-500 font-bold mt-1">Total: R$ {finalPrice.toFixed(2).replace('.', ',')}</p>
                     </div>
                  ) : (
                    <div className="flex justify-between items-end">
                        <span className="text-sm font-bold">Total a Pagar</span>
                        <span className="text-3xl font-black text-white">R$ {finalPrice.toFixed(2).replace('.',',')}</span>
                    </div>
                  )}
               </div>

               <div className="mt-8 space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Possui um cupom?</label>
                  <div className="flex gap-2">
                     <div className="relative flex-1">
                        <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                        <input 
                           disabled={!!appliedCoupon}
                           value={couponCode}
                           onChange={e => setCouponCode(e.target.value)}
                           placeholder="CÓDIGO" 
                           className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-xs font-black uppercase outline-none focus:border-parliament-400 disabled:opacity-50" 
                        />
                     </div>
                     {appliedCoupon ? (
                        <button onClick={() => { setAppliedCoupon(null); setCouponCode(''); }} className="p-3 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/40 transition-all"><X size={16}/></button>
                     ) : (
                        <button onClick={handleApplyCoupon} disabled={couponLoading || !couponCode} className="px-4 bg-white text-navy-950 rounded-xl text-[10px] font-black uppercase hover:bg-parliament-400 hover:text-white transition-all">
                           {couponLoading ? <Loader2 size={14} className="animate-spin"/> : 'Aplicar'}
                        </button>
                     )}
                  </div>
                  {couponError && <p className="text-[10px] text-red-400 font-bold ml-1">{couponError}</p>}
                  {appliedCoupon && <p className="text-[10px] text-emerald-400 font-black ml-1 uppercase tracking-widest flex items-center gap-1"><Check size={12}/> Cupom aplicado com sucesso!</p>}
               </div>
            </div>
            
            <div className="mt-10">
               <div className="flex items-center gap-3 text-emerald-400 mb-2">
                  <ShieldCheck size={20} />
                  <span className="text-[10px] font-black uppercase tracking-widest">Ambiente Criptografado</span>
               </div>
               <p className="text-[9px] text-slate-500 font-medium leading-relaxed">
                  Seus dados são processados via Supabase Edge Functions. Sua chave de acesso nunca deixa o servidor.
               </p>
            </div>
          </div>

          <div className="lg:col-span-3 p-10 overflow-y-auto max-h-[80vh] custom-scrollbar">
             <form onSubmit={handleProcessPayment} className="space-y-8">
                <div className="space-y-3">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Forma de Pagamento</label>
                   <div className="grid grid-cols-3 gap-3">
                      {[
                        { id: 'CREDIT_CARD', label: 'Cartão', icon: CreditCard },
                        { id: 'PIX', label: 'PIX', icon: QrCode },
                        { id: 'BOLETO', label: 'Boleto', icon: Barcode },
                      ].map(m => (
                        <button key={m.id} type="button" onClick={() => { setMethod(m.id as any); if(m.id !== 'CREDIT_CARD') setInstallments(1); }} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all ${method === m.id ? 'border-parliament-600 bg-parliament-50 dark:bg-parliament-900/20 text-parliament-600' : 'border-neutral-100 dark:border-navy-800 text-slate-400 hover:border-neutral-200'}`}><m.icon size={20} /><span className="text-[9px] font-black uppercase">{m.label}</span></button>
                      ))}
                   </div>
                </div>

                {/* Seletor de Parcelas - Apenas para Anual no Cartão */}
                {method === 'CREDIT_CARD' && billingCycle === 'yearly' && (
                  <div className="space-y-3 animate-in slide-in-from-top-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><CreditCard size={12}/> Opções de Parcelamento</label>
                     <div className="relative group">
                        <select 
                          value={installments} 
                          onChange={e => setInstallments(Number(e.target.value))}
                          className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600 appearance-none"
                        >
                           {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(i => (
                             <option key={i} value={i}>{i}x de R$ {(finalPrice / i).toFixed(2).replace('.', ',')} {i === 1 ? '(À vista)' : '(Sem juros)'}</option>
                           ))}
                        </select>
                        <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                     </div>
                  </div>
                )}

                <div className="space-y-4">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Dados de Faturamento</p>
                   <div className="grid grid-cols-1 gap-4">
                      <input required placeholder="Nome Completo / Razão Social" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={billingInfo.name} onChange={e => setBillingInfo({...billingInfo, name: e.target.value})} />
                      <div className="grid grid-cols-2 gap-4">
                        <input required placeholder="CPF ou CNPJ" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={billingInfo.cpfCnpj} onChange={e => setBillingInfo({...billingInfo, cpfCnpj: e.target.value})} />
                        <input required placeholder="Telefone (com DDD)" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={billingInfo.mobilePhone} onChange={e => setBillingInfo({...billingInfo, mobilePhone: e.target.value})} />
                      </div>
                      <input required type="email" placeholder="E-mail para faturas" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={billingInfo.email} onChange={e => setBillingInfo({...billingInfo, email: e.target.value})} />
                      <div className="grid grid-cols-2 gap-4">
                        <input required placeholder="CEP" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={billingInfo.postalCode} onChange={e => setBillingInfo({...billingInfo, postalCode: e.target.value})} />
                        <input required placeholder="Número" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={billingInfo.addressNumber} onChange={e => setBillingInfo({...billingInfo, addressNumber: e.target.value})} />
                      </div>
                   </div>
                </div>

                {method === 'CREDIT_CARD' && (
                  <div className="space-y-4 animate-in slide-in-from-top-2">
                     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Dados do Cartão</p>
                     <div className="space-y-4">
                        <input required placeholder="Nome no Cartão" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={cardInfo.holderName} onChange={e => setCardInfo({...cardInfo, holderName: e.target.value})} />
                        <input required placeholder="Número do Cartão" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" value={cardInfo.number} onChange={e => setCardInfo({...cardInfo, number: e.target.value})} />
                        <div className="grid grid-cols-3 gap-4">
                           <input required placeholder="Mês (MM)" maxLength={2} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600 text-center" value={cardInfo.expiryMonth} onChange={e => setCardInfo({...cardInfo, expiryMonth: e.target.value})} />
                           <input required placeholder="Ano (AAAA)" maxLength={4} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600 text-center" value={cardInfo.expiryYear} onChange={e => setCardInfo({...cardInfo, expiryYear: e.target.value})} />
                           <input required placeholder="CCV" maxLength={4} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl px-5 py-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600 text-center" value={cardInfo.ccv} onChange={e => setCardInfo({...cardInfo, ccv: e.target.value})} />
                        </div>
                     </div>
                  </div>
                )}

                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl text-red-600 dark:text-red-400 text-xs font-bold flex items-center gap-3"><AlertCircle size={18} /> {error}</div>
                )}

                <button disabled={loading} className="w-full py-5 bg-parliament-600 text-white rounded-3xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-parliament-600/20 flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50">
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <><CheckCircle2 size={20} /> FINALIZAR ASSINATURA</>}
                </button>
             </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;